import { VpIcon, VpIconButton } from '@vtmn-play/react'
import { useEffect, useRef, useState } from 'react'
import { ManagementDialog } from '@/components/FavoriteViews/ManagementDialog'
import './favorite-view-management-menu.scss'

interface FavoriteViewManagementMenuProps {
  accessibleName: string
  dialogType: 'folder' | 'view'
  menuId: string
  menuTestId: string
  onDelete: () => void
  onRename: () => void
  onShare?: () => void
  renameTestId: string
  deleteTestId: string
  shareTestId?: string
  triggerTestId: string
}

export function FavoriteViewManagementMenu({
  accessibleName,
  dialogType,
  menuId,
  menuTestId,
  onDelete,
  onRename,
  onShare,
  renameTestId,
  deleteTestId,
  shareTestId,
  triggerTestId,
}: Readonly<FavoriteViewManagementMenuProps>) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen)
      return

    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target))
        setIsOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div className="favorite-page-menu-container" data-dialog-type={dialogType} ref={menuRef}>
      <VpIconButton
        ref={triggerRef}
        type="button"
        className="favorite-page-menu-trigger"
        variant="tertiary"
        size="medium"
        aria-label={accessibleName}
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen(open => !open)}
        data-testid={triggerTestId}
      >
        <VpIcon name="more" />
      </VpIconButton>
      {isOpen && (
        <div
          id={menuId}
          className="favorite-page-menu"
          role="group"
          aria-label={accessibleName}
          data-testid={menuTestId}
        >
          <ManagementDialog
            onRename={() => {
              setIsOpen(false)
              onRename()
            }}
            onDelete={() => {
              onDelete()
              setIsOpen(false)
            }}
            {...(onShare
              && {
                onShare: () => {
                  onShare()
                  setIsOpen(false)
                },
              })}
            renameTestId={renameTestId}
            deleteTestId={deleteTestId}
            shareTestId={shareTestId}
          />
        </div>
      )}
    </div>
  )
}
